import React from 'react';
import { FormattedInput, Input, ValidationState } from '../../../components/Forms';
import styled from 'styled-components/macro';
import { border, colors, distances } from '../../../styles/constants';
import { useTranslation } from 'react-i18next';
import { CurrencyCodeRecord } from 'currency-codes';
import { getValueAt } from '../../../helpers/getValueAt';
import { DetailedItemValue } from '../types';
import { itemsVatAmountExp } from './vat';
import { Button } from '../../../components/Buttons';
import { numberFormat, getLocaleNumeralDelimiter, getLocaleNumeralDecimal } from '../../../helpers/intl';
import Icon from '../../../components/Icons';

interface DetailedItemProps {
    item: DetailedItemValue;
    namePrefix: string;
    onChange: (e: React.FormEvent<HTMLInputElement>) => void;
    onBlur: (e: React.FormEvent<HTMLInputElement>) => void;
    onRemove: (id: string) => void;
    className?: string;
    currency: CurrencyCodeRecord;
    touched: any;
    errors: any;
    autoFocus: boolean;
}

const validation = (path: string, errors: any, touched: any) => {
    const error = getValueAt(path, errors);
    const was_touched = getValueAt(path, touched);
    const validation = error
        ? {
            state: ValidationState.Invalid,
            message: error,
        }
        : undefined;
    return was_touched && validation;
};

const DetailedItem = (
    {
        item,
        onChange,
        namePrefix,
        className,
        currency,
        onBlur,
        errors,
        touched,
        onRemove,
        autoFocus,
    }: DetailedItemProps
) => {
    const { t, i18n } = useTranslation();
    const lng = i18n.language;

    return (
        <>
            <ItemWrapper className={className}>
                <Wrapper className="w-25">
                    <Input
                        name={`${namePrefix}.description`}
                        label={t('payments.virtual_terminal.fields.items.description')}
                        placeholder=""
                        value={item.description}
                        onChange={onChange}
                        validation={validation('description', errors, touched)}
                        onBlur={onBlur}
                        autoFocus={autoFocus}
                    />
                </Wrapper>
                <Wrapper className="w-15">
                    <FormattedInput
                        name={`${namePrefix}.quantity`}
                        label={t('payments.virtual_terminal.fields.items.quantity')}
                        placeholder="1"
                        value={item.quantity}
                        type="number"
                        validation={validation('quantity', errors, touched)}
                        onChange={onChange}
                        onBlur={onBlur}
                        options={{
                            numericOnly: true,
                            numeralPositiveOnly: true,
                        }}
                    />
                </Wrapper>
                <Wrapper className="w-15">
                    <FormattedInput
                        name={`${namePrefix}.amount`}
                        label={t('payments.virtual_terminal.fields.items.amount')}
                        placeholder="123"
                        value={item.amount}
                        type="tel"
                        inputMode="numeric"
                        onChange={onChange}
                        onBlur={onBlur}
                        validation={validation('amount', errors, touched)}
                        autoComplete="off"
                        options={{
                            numeral: true,
                            numericOnly: true,
                            numeralThousandsGroupStyle: 'thousand',
                            delimiter: getLocaleNumeralDelimiter(lng),
                            numeralIntegerScale: 6,
                            numeralDecimalMark: getLocaleNumeralDecimal(lng),
                            numeralPositiveOnly: true,
                            numeralDecimalScale: currency.digits,
                        }}
                    />
                </Wrapper>
                <Wrapper className="w-15">
                    <FormattedInput
                        name={`${namePrefix}.vat`}
                        label={t('payments.virtual_terminal.fields.items.vat')}
                        placeholder=""
                        value={item.vat}
                        type="tel"
                        validation={validation('vat', errors, touched)}
                        inputMode="numeric"
                        onChange={onChange}
                        onBlur={onBlur}
                        prefix=""
                        options={{
                            numeral: true,
                            numeralPositiveOnly: true,
                        }}
                    />
                </Wrapper>
                <Wrapper className="w-15">
                    <Input
                        name={`${namePrefix}.vat`}
                        label={t('payments.virtual_terminal.fields.items.vat_amount')}
                        value={item.vat ? numberFormat(lng).format(itemsVatAmountExp([item], lng, currency.code) / 100) : '0'}
                        type="tel"
                        placeholder="123"
                        inputMode="numeric"
                        onChange={onChange}
                        onBlur={onBlur}
                        disabled
                    />
                </Wrapper>
                <Wrapper className="w-15-edge wide">
                    <RemoveItem title={t('payments.virtual_terminal.buttons.remove_item')}>
                        <Button type="button" onClick={() => onRemove(item.line_id)} className="alt tiny">
                            <Icon icon="close_small" fill={colors.invalid} />
                        </Button>
                    </RemoveItem>
                </Wrapper>
                <MobileButtonWrapper>
                    <Button className="alt" type="button" onClick={() => onRemove(item.line_id)}>
                        {t('payments.virtual_terminal.buttons.remove_item')}
                    </Button>
                </MobileButtonWrapper>
            </ItemWrapper>
            <Hr />
        </>
    );
};

const RemoveItem = styled.div`
    margin-left: 5px;
    padding-top: 28px;
    align-items: flex-end;
    display: flex;
    justify-content: flex-end;
`;

const ItemWrapper = styled.div`
    display: flex;
    flex-direction: row;

    @media (max-width: 1100px) {
        display: block;
    }
`;

const Wrapper = styled.div`
    margin-right: 5px;

    &.w-25 {
        width: 25%;
        @media (max-width: 1100px) {
            width: 100%;
        }
    }
    &.w-15 {
        width: 15%;

        @media (max-width: 1100px) {
            width: 100%;
        }
    }
    &.w-15-edge {
        width: 15%;
        margin-right: 0;

        @media (max-width: 1100px) {
            width: 100%;
        }
    }

    @media (max-width: 1100px) {
        &.wide {
            display: none;
        }
    }
`;

const Hr = styled.hr`
    width: 100%;
    margin-bottom: ${distances.small};
    border: none;
    border-bottom: ${border.double} solid ${colors.borderLight};

    @media (max-width: 1100px) {
        margin-bottom: ${distances.normal};
    }
`;

const MobileButtonWrapper = styled.div`
    display: flex;
    margin: ${distances.normal};
    justify-content: center;

    @media (min-width: 1101px) {
        display: none;
    }
`;

export default DetailedItem;
